import * as _ from "underscore";

import * as dateUtils from "./dateUtils.js";

let _wordsPerDay = 25;

export const DAY = 24 * 3600 * 1000;
export const INTERVAL_1 = 1 * DAY;
export const INTERVAL_2 = 6 * DAY;
export const GETTING_THERE_INTERVAL = INTERVAL_1 + DAY;
export const MASTERED_INTERVAL = INTERVAL_2 + DAY;
export const NEW_WORD_AVERAGE = DAY / INTERVAL_1;

const easinessMultiplier = (interval) => {
  const inflectionPoint = 3 * DAY;

  if (interval < inflectionPoint) {
    return 3 - 2 * (interval / inflectionPoint);
  } else {
    return 1;
  }
};

// ease is a number between 0 and 5
// 0 = complete fail
// 5 = perfect recall
// based on the SM2 algorithm
//
// strength: 1 = active, 0.2 = passive, 0 = does nothing
/**
 * Processes a word recall event, updating the word's spaced repetition data.
 *
 * This function implements a modified SM2 spaced repetition algorithm to update scheduling based on recall quality.
 *
 * @param {Object} userWord - The user's word object to update
 * @param {number} ease - A measure of recall quality, from 0 (complete fail) to 5 (perfect recall)
 * @param {Date} currentDate - The current date when the recall happened
 * @param {number} [strength=1] - Strength of the recall (1 = active, 0.2 = passive, 0 = does nothing)
 * @param {number} [intervalOverride=-1] - If provided, overrides the calculated interval
 * @param {boolean} [addJitter=false] - Whether to add random variation to the interval
 * @returns {number} The number of cards to wait before showing this word again in the current session, or -1 if immediate
 */
export const wordRecalled = (
  userWord,
  ease,
  currentDate,
  strength = 1,
  intervalOverride = undefined,
  addJitter = false
) => {
  let actualInterval = 0;

  /** @type {any} */
  const userWordData = _.pick(
    userWord.toJSON(),
    "easinessFactor recallAttempts interval lastRecallEase nextDate"
  );

  if (typeof strength === "undefined") {
    strength = 1;
  }

  // initialise values
  userWordData.easinessFactor = userWord.has("easinessFactor")
    ? userWord.get("easinessFactor")
    : 2.5;
  userWordData.recallAttempts = userWord.has("recallAttempts")
    ? userWord.get("recallAttempts")
    : 0;
  userWordData.interval = userWord.has("interval")
    ? userWord.get("interval")
    : 0;
  userWordData.lastRecallEase = userWord.has("lastRecallEase")
    ? userWord.get("lastRecallEase")
    : 0;

  let currentTime = currentDate.getTime();

  if (userWord.has("nextDate")) {
    const lastTime =
      new Date(dateUtils.parse(userWord.get("nextDate"))).getTime() -
      userWord.get("interval");

    actualInterval = currentTime - lastTime;

    // shift back currentDate depending on strength
    //   strength = 1:    normal
    //   strength = 0.5:  equivalent to remembering it half way between the last recall date and now

    currentTime = currentTime * strength + (1 - strength) * lastTime;
  }

  let strengthFactor = strength;
  if (actualInterval > 0 && userWordData.interval > 0) {
    strengthFactor *= Math.min(1, actualInterval / userWordData.interval);
  }

  // This is really just a record of the last time we practiced this word
  userWordData.previousIntervalDate = currentDate.toISOString();

  if (userWordData.interval > 0) {
    userWordData.previousInterval = userWordData.interval;
    userWordData.easinessFactor = Math.max(
      1.3,
      userWordData.easinessFactor +
        strengthFactor * (0.1 - (5 - ease) * (0.08 + (5 - ease) * 0.02))
    );
  }

  let cardsToWaitThisSession = -1;

  if (intervalOverride > -1) {
    userWordData.interval = intervalOverride;
    if (ease < 1) {
      cardsToWaitThisSession = 5;
    } else {
      cardsToWaitThisSession = 100;
    }
  } else if (ease < 1) {
    userWordData.interval = -1000;
    cardsToWaitThisSession = 8;
  } else if (ease < 3) {
    userWordData.interval = -1000;
    cardsToWaitThisSession = 50;
  } else {
    const minAmount = (12 + 12 * Math.max(0, ease - 4)) * 3600 * 1000;

    if (actualInterval <= 0) {
      userWordData.interval = Math.floor(strength * minAmount);
    } else {
      userWordData.interval = Math.floor(
        Math.max(
          strength * minAmount,
          Math.max(actualInterval, userWordData.interval) *
            (easinessMultiplier(actualInterval) *
              userWordData.easinessFactor *
              strengthFactor +
              1 -
              strengthFactor)
        )
      );
    }

    if (addJitter) {
      // Add jitter of between -10% and +10% to the interval
      userWordData.interval += Math.floor(
        (Math.random() - 0.5) * 0.2 * userWordData.interval
      );
    }
  }

  userWordData.lastRecallEase = ease;
  userWordData.recallAttempts = userWordData.recallAttempts + 1;

  userWordData.nextDate = new Date(
    currentTime + userWordData.interval
  ).toISOString();

  userWord.save(userWordData, { patch: true });

  return cardsToWaitThisSession;
};
/*
  // prepare a test from the list of words,
  // assumes they are all of the correct language
  var prepareTest = function (userWords, currentDate) {
    console.log("preparing test");
    var readyToTestArray = [];

    // keep notStarted sorted by word frequency
    
    words.notStarted.comparator = function (userWord) {
      return - (userWord.attributes.frequency || 0);
    };
    var averagePerDay = 0;

    userWords.each(function (word) {
      _.each(calcGroups(word, currentDate), function (groupName) {
        if (groupName === "readyToTest") {
          readyToTestArray.push(word);
        } else {
          words[groupName].push(word);
        }
      });

      averagePerDay += word.perDayAverage(currentDate);
    });

    // restrict number of new words to learn at once
    var extraWordsToTest = Math.min((_wordsPerDay - averagePerDay) / NEW_WORD_AVERAGE,
        words.notStarted.length);

    _.each(_.shuffle(readyToTestArray), function (word) {
      words.readyToTest.push(word);
    });

    // top up readyToTest with null words
    _.each(_.range(extraWordsToTest), function () {
      words.readyToTest.push(new UserWord());
    });

    words.notStarted.sort();

    return {
      averagePerDay: averagePerDay,
      groups: words
    };
  };*/

export const getIntervalName = (interval) => {
  if (typeof interval === "undefined") {
    return "notStarted";
  } else if (interval < GETTING_THERE_INTERVAL) {
    return "justStarted";
  } else if (interval < MASTERED_INTERVAL) {
    return "gettingThere";
  } else {
    return "mastered";
  }
};

/**
 * @returns {string[]} the list of names of the groups that `userWord` belongs to
 */
export const calcGroups = (userWord, currentDate) => {
  const groupNames = [];

  if (typeof userWord.get("nextDate") === "undefined") {
    groupNames.push("notStarted");
    groupNames.push("readyToTest");
  } else {
    const nextDate = new Date(dateUtils.parse(userWord.get("nextDate")));
    if (nextDate.getTime() < currentDate.getTime()) {
      groupNames.push("readyToTest");
    } else {
      //    groupNames.push('scheduled');
    }
    groupNames.push(getIntervalName(userWord.get("interval")));
  }

  return groupNames;
};

export const wordsPerDay = (value) => {
  if (typeof value === "undefined") {
    return _wordsPerDay;
  } else {
    _wordsPerDay = value;
  }
};
